import {
  Box,
  CollapsibleCard,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { CheckCircleIcon, TrendingNeutralIcon } from '@atgfr8/icons';
import {
  Button,
  Notification,
  RadioGroup,
  Tab,
  TabsComponent,
  ToastType,
} from '@components';
import { AtgLoader } from '@components/AtgLoader';
import CommonFileUpload, {
  FileTypes,
} from '@components/FileUpload/CommonFileUpload';
import { useNavigate } from '@solidjs/router';
import { createAppStore } from '@store/createAppStore';
import { ICustomerDetails } from '@store/customers/customerDetails';
import { CreateCustomerState } from '@store/customers/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeCustomerDetails } from '@utils/routeCustomerDetails';
import {
  handleToast,
  isAdmin,
  isFullAdmin,
  setSameAsOtherAddress,
} from '@utils/utils';
import { MailingOrPhysicalAddress } from '@views/customer/customerDetail';
import { isEmpty } from 'lodash';
import { createSignal, For, Match, Show, Switch } from 'solid-js';

import classes from './classes';
import { CustomerContact, CustomerHeader } from './components';
import OwnerShipTab from './components/OwnerShipTab';
import { initialState } from './constants';

export const [createCustomerStore, setCreateCustomerStore] =
  createAppStore<CreateCustomerState>(
    { ...initialState },
    'createCustomerStore',
  );

export const setupNewCustomerState = (create: Partial<ICustomerDetails>) => {
  Object.entries(create).forEach(([key, value]) => {
    setCreateCustomerStore('customer', key as keyof ICustomerDetails, value);
  });
};

const acceptedTypes = [
  'pdf',
  'doc',
  'jpg',
  'docx',
  'xlsx',
  'csv',
  'tif',
  'xks',
  'html',
  'png',
  'jpeg',
  'msg',
  'odt',
  'pptx',
];

const NewCustomerTabPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = createSignal(0);
  const [isDoneTabReady, setIsDoneTabReady] = createSignal(false);
  const [errors, setErrors] = createSignal<string[]>([]);

  const [file, setFile] = createSignal<File[]>([]);
  const [paymentType, setPaymentType] = createSignal(
    Boolean(createCustomerStore.customer.paymentType)
      ? String(createCustomerStore.customer.paymentType)
      : '0',
  );
  const [loadPending, setLoadPending] = createSignal(
    createCustomerStore.customer.loadPending === true ? 'true' : 'false',
  );

  const sameAsOthersAddress = (sourceType: 'mailing' | 'physical') => {
    setSameAsOtherAddress(
      sourceType,
      createCustomerStore,
      setupNewCustomerState,
    );
  };

  const tabs = [
    'Name And Address',
    'Confirm Contacts Information',
    'Upload Credit Information',
    'Ownership',
    'Done',
  ];

  const handleNext = () => {
    if (activeTab() === 3) {
      setIsDoneTabReady(true);
      setActiveTab(4);
    } else {
      setActiveTab(activeTab() + 1);
    }
  };

  const requiredContactTypes = [
    {
      type: 'Billing',
      message: 'You must have at least one billing contact.',
    },
    {
      type: 'Freight',
      message: 'You must have at least one freight contact.',
    },
  ];

  const validateFields = () => {
    const {
      name,
      mailingAddress1,
      mailingCity,
      mailingState,
      mailingZip,
      contacts,
      accountManagerName,
      salesManagerName,
    } = createCustomerStore.customer;

    const requiredFields = [
      { value: name, message: 'Customer name is required.' },
      {
        value: accountManagerName,
        message: 'Account manager is required.',
      },
      { value: salesManagerName, message: 'Sales manager is required.' },
      {
        value: mailingAddress1,
        message: 'Mailing address line 1 is required.',
      },
      { value: mailingCity, message: 'Mailing address city is required.' },
      { value: mailingState, message: 'Mailing address state is required.' },
      { value: mailingZip, message: 'Mailing address zip code is required.' },
    ];

    const errors = requiredFields
      .filter((field) => isEmpty(field.value))
      .map((field) => field.message);

    requiredContactTypes.forEach(({ type }) => {
      if (!contacts?.some((contact) => contact.type === type)) {
        errors.push(
          `Customer has no ${type.toLowerCase()} contact information.`,
        );
      }
    });

    setErrors(errors);
  };

  const handleClick = () => {
    const isNative = routeCustomerDetails(
      Number(createCustomerStore.responseData.id),
    );
    if (isNative)
      navigate(`/customer/details/${createCustomerStore.responseData.id}`);
  };

  const FinalTab = () => {
    return (
      <Box class={classes.finalTabContainer}>
        <Show when={!createCustomerStore.isLoading} fallback={<AtgLoader />}>
          <Show when={errors().length > 0}>
            <Box class={classes.errorBox}>
              <Typography class={classes.errorHeading}>
                Please correct the following problems.
              </Typography>
              <ul class={classes.listStyle}>
                <For each={errors()}>{(error) => <li>{error}</li>}</For>
              </ul>
            </Box>
          </Show>
          <Show when={createCustomerStore.responseData.id}>
            <Box class={classes.successMessageBox}>
              <Box class={classes.successHeadingBox}>
                <CheckCircleIcon class={classes.circleIconStyle} />
                <span>Done!</span>
              </Box>
              <Typography class={classes.successDescription}>
                Customer was submitted to the corporate credit department for
                review. Reviews may take up to 24 hours before credit is
                assigned. You will receive a notification when they have
                addressed your customer.
              </Typography>
              <Button
                size="lg"
                variant="contained"
                label="Go to Customer Profile"
                onClick={handleClick}
                dataTestId={formatDataTestId(
                  'Go to Customer Profile',
                  'button',
                )}
              />
            </Box>
          </Show>
        </Show>
      </Box>
    );
  };

  const removeFile = (index: number) => {
    setFile((prev) => {
      const files = [...prev];
      files.splice(index, 1);
      return files;
    });
  };

  const customFileUploadValidation = (singleFile: File) => {
    if (Boolean(file().some((f) => f.name === singleFile.name))) {
      handleToast(ToastType.Error, 'File already exists');
      return false;
    }
    return true;
  };

  const handleContact = () => {
    if (activeTab() === 1) {
      const contacts = createCustomerStore.customer.contacts ?? [];
      const missingContact = requiredContactTypes.find(
        ({ type }) => !contacts.some((contact) => contact.type === type),
      );
      if (missingContact) {
        handleToast(ToastType.Error, missingContact.message);
        return;
      }
    }
    handleNext();
  };

  return (
    <Box>
      <TabsComponent
        tabs={tabs.map((item, index) => (
          <Tab>
            {index + 1}. {item}
          </Tab>
        ))}
        activeTab={activeTab()}
        border={true}
        tabStyleClasses="rounded-t-lg"
        activeStyling="bg-[#ffe8b7]"
        onTabClick={(index) => {
          if (createCustomerStore.responseData.id) return;
          if (index !== 4 || isDoneTabReady()) {
            setActiveTab(index);
          }
        }}
      />
      <div class="border-t border-gray-300" />
      <div class="py-2"></div>
      <Switch>
        <Match when={activeTab() === 0}>
          <Box class={classes.container}>
            <CustomerHeader
              title="Name And Address"
              subTitle="Please verify the name and address."
            />
            <Box class="flex flex-col gap-2 mb-[20px]">
              <TextField
                label="Name"
                placeholder="Name"
                value={createCustomerStore.customer.name ?? ''}
                onChange={(val: string) => {
                  setupNewCustomerState({ name: val });
                }}
                class={classes.nameValue}
                maxLength={50}
                dataTestId="new-customer-tab-panel-name-textInput"
              />
              <TextField
                label="Legal Name"
                placeholder="Legal Name"
                value={createCustomerStore.customer.legalName ?? ''}
                onChange={(val: string) => {
                  setupNewCustomerState({ legalName: val });
                }}
                class={classes.nameValue}
                maxLength={50}
                dataTestId="new-customer-tab-panel-legal-name-textInput"
              />
              <RadioGroup
                value={paymentType()}
                onChange={(value) => {
                  setPaymentType(value);
                  setupNewCustomerState({ paymentType: value });
                }}
                options={[
                  { label: 'I will need credit for this customer', value: '0' },
                  { label: 'This customer is Prepaid', value: '1' },
                ]}
              />
              <Typography class=" text-xl! font-medium!">
                Pending Load
              </Typography>
              <Notification
                type="info"
                text="Does this customer have a pending load? "
                sxProps="w-fit!"
              />
              <RadioGroup
                value={loadPending()}
                onChange={(value) => {
                  setLoadPending(value);
                  setupNewCustomerState({ loadPending: value === 'true' });
                }}
                options={[
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' },
                ]}
              />

              <Stack direction="row" gap={2}>
                <Box flex={1}>
                  <MailingOrPhysicalAddress
                    addressType="mailing"
                    buttonLabel="SAME AS PHYSICAL"
                    setSameAsOther={() => sameAsOthersAddress('physical')}
                    customerStore={createCustomerStore}
                    customerFormState={setupNewCustomerState}
                    isCreateCustomer={true}
                  />
                </Box>
                <Box flex={1}>
                  <MailingOrPhysicalAddress
                    addressType="physical"
                    buttonLabel="SAME AS MAILING"
                    setSameAsOther={() => sameAsOthersAddress('mailing')}
                    customerStore={createCustomerStore}
                    customerFormState={setupNewCustomerState}
                    isCreateCustomer={true}
                  />
                </Box>
              </Stack>

              <Box class={classes.nextBtn}>
                <Button
                  size="lg"
                  endIcon={<TrendingNeutralIcon />}
                  variant="contained"
                  label="Next"
                  onClick={handleNext}
                  dataTestId={formatDataTestId('Next', 'button')}
                />
              </Box>
            </Box>
          </Box>
        </Match>
        <Match when={activeTab() === 1}>
          <Box class={classes.container}>
            <CustomerHeader
              title="Confirm Contacts"
              subTitle="You are required to have at least one billing contact and one freight contact."
            />
            <Box class="max-w-[600px]">
              <CustomerContact />
            </Box>
            <Divider />
            <Box class={classes.btnBox}>
              <Button
                size="lg"
                startIcon={<TrendingNeutralIcon class="rotate-180" />}
                variant="contained"
                label="Previous"
                onClick={() => setActiveTab(0)}
                dataTestId={formatDataTestId('Previous', 'button')}
              />
              <Button
                size="lg"
                endIcon={<TrendingNeutralIcon />}
                variant="contained"
                label="Next"
                onClick={handleContact}
                dataTestId={formatDataTestId('Next', 'button')}
              />
            </Box>
          </Box>
        </Match>
        <Match when={activeTab() === 2}>
          <Box class={classes.container}>
            <CustomerHeader
              title="Upload Credit Documents"
              subTitle="Please upload any documents we will need to review and assign
                credit."
            />
            <Box class="max-w-[600px]">
              <CollapsibleCard
                startTitle="Upload Documents"
                accordion={false}
                class="mb-2"
              >
                <CommonFileUpload
                  height={5}
                  uploadedFiles={file()}
                  id="importCustomerDoc"
                  shouldUploadOnSelect={false}
                  isMultipleUpload={true}
                  acceptedFileTypes={acceptedTypes as FileTypes[]}
                  apiCallBackFn={async (file: File) => {
                    await setFile((prev) => {
                      return [...prev, file];
                    });
                  }}
                  removeFile={removeFile}
                  customValidation={customFileUploadValidation}
                  isRemoveFile={isAdmin()}
                  isAdmin={isFullAdmin()}
                />
              </CollapsibleCard>
            </Box>
            <Divider />
            <Box class={classes.btnBox}>
              <Button
                size="lg"
                startIcon={<TrendingNeutralIcon class="rotate-180" />}
                variant="contained"
                label="Previous"
                onClick={() => setActiveTab(1)}
                dataTestId={formatDataTestId('Previous', 'button')}
              />
              <Button
                size="lg"
                endIcon={<TrendingNeutralIcon />}
                variant="contained"
                label="Next"
                onClick={handleNext}
                dataTestId={formatDataTestId('Next', 'button')}
              />
            </Box>
          </Box>
        </Match>
        <Match when={activeTab() === 3}>
          <OwnerShipTab
            setActiveTab={setActiveTab}
            validateFields={validateFields}
            file={file}
          />
        </Match>
        <Match when={activeTab() == 4}>
          <Box class="min-h-[400px] p-3">
            <CustomerHeader title="Finished" />
            <FinalTab />
          </Box>
        </Match>
      </Switch>
    </Box>
  );
};

export default NewCustomerTabPanel;
